
import { Component, Mixins, Watch } from "vue-property-decorator";
import CreateEditShift from "./modals/create-edit-shift.vue";
import InactiveSubscriptionModal from "@/common/components/inactive-subscription-modal.vue";
import { mapState } from "vuex";
import ShiftKanban from "./shift-kanban.vue";
import ShiftTable from "./shift-table.vue";
import ShiftMixin from "./shift.mixin";
import AuthMixin from "@/common/mixins/auth.mixin";
import { OfficeDto } from "@/types/office.type";
import { ShiftDataDto, ShiftGroupFormDto } from "@/types/shift.type";
import { SubscriptionStatus } from "@/common/constants";
import { AcceptedShift, RequestedShift } from "./shift.types";

interface ShiftBoardSettings {
  isKanban: boolean;
}

@Component({
  components: {
    CreateEditShift,
    InactiveSubscriptionModal,
    ShiftKanban,
    ShiftTable
  },
  computed: {
    ...mapState("Shift", 
    [
      "selectedOffice",
      "offices",
      "loading",
      "shiftData",
      "confirmedShifts",
      "requestedShifts"
    ])
  }
})
export default class ShiftComponent extends Mixins(ShiftMixin, AuthMixin) {
  selectedOffice!: OfficeDto;
  offices!: OfficeDto[];
  confirmedShifts!: AcceptedShift[];
  requestedShifts!: RequestedShift[];
  shiftData!: ShiftDataDto;
  loading!: boolean;

  hasConfirmedShift = false;
  initialized = false;
  showCurrentUserShifts = false;
  isKanban = false;
  isEdit = false;

  async created(): Promise<void> {
    this.setLoading(true);
    
    await this.$store.dispatch("Shift/getShiftData");
    await this.$store.dispatch("Shift/getDeleteFormData");
    await this.$store.dispatch("Shift/getOffices", { userId: this.userInfo()?.sub });

    if (!this.offices?.length) {
      this.$router.replace({ path: '/office-profiles' });

      return;
    }

    const shiftBoardSettingsRaw = sessionStorage.getItem("shift-board-settings");

    if (shiftBoardSettingsRaw) {
      const shiftBoardSettings = JSON.parse(shiftBoardSettingsRaw) as ShiftBoardSettings;

      this.isKanban = shiftBoardSettings.isKanban;
    }

    await this.fetchData();

    this.setLoading(false);

    this.initialized = true;

    this.tryShowInactiveSubsriptionModal();
  }

  @Watch("selectedOffice")
  onOfficeChange(): void {
    this.tryShowInactiveSubsriptionModal();
  }

  tryShowInactiveSubsriptionModal(): void {
    const subscriptionTrialingStatus = SubscriptionStatus.Trialing.getId(this.shiftData.subscriptionStatuses);
    const subscriptionActiveStatus = SubscriptionStatus.Active.getId(this.shiftData.subscriptionStatuses);

    if (this.selectedOffice.subscriptionStatus !== subscriptionTrialingStatus && this.selectedOffice.subscriptionStatus !== subscriptionActiveStatus && this.selectedOffice.isBillable) {
      this.$bvModal.show("inactive-subscription-modal");
    }
  }

  async fetchData(): Promise<void> {
    if (this.isKanban) {
      await this.getShifts(this.isKanban, this.selectedOffice.id);
    }
    else {
      await this.getShifts(this.isKanban);
    }
  }

  updateViewValue(value: boolean): void {
    this.isKanban = value;
    sessionStorage.setItem("shift-board-settings", JSON.stringify({ isKanban: this.isKanban }));
    this.fetchData();
  }

  async onOfficeSelect(value: OfficeDto): Promise<void> {
    this.$store.commit("Shift/updateField", {
      path: "selectedOffice",
      value: value
    });
  }

  async onEdit(shiftGroupId: number): Promise<void> {
    await this.$store.dispatch("Shift/getShiftForm", shiftGroupId);

    this.isEdit = true;
    this.$bvModal.show("create-edit-shift-modal");
  }

  showShiftSaveModal(): void {
     this.$store.commit("Shift/updateField", {
      path: "shiftForm",
      value: new ShiftGroupFormDto()
    });

    this.isEdit = false;
    this.$bvModal.show("create-edit-shift-modal");
  }

  async refreshShifts(): Promise<void> {
    this.setLoading(true);

    if (this.isKanban) {
      await this.getShifts(this.isKanban, this.selectedOffice.id);
    }
    else {
      await this.getShifts(this.isKanban)
    }

    this.setLoading(false);
  }
}
